import { ComponentType, MouseEventHandler } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { PosAnimatingProgressBar } from 'src/core/POS/PosProgressBar/PosAnimatingProgressBar';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { PosIconProps } from 'src/svgs/SvgWrapper';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { Event, SponsoredListingsCampaign } from 'src/WebApiController';

import { BoostIconDisplay } from '../BoostIconDisplay';
import * as styles from './BoostListingTabButton.css';

export type BoostListingTabButtonProps = {
  campaign: SponsoredListingsCampaign;
  event: Event;
  isActive: boolean;
  zoneIcon: ComponentType<PosIconProps>;
  zoneTooltip?: string;
  compact?: boolean;
  onClick: MouseEventHandler;
};

/**
 * A tab button for a single campaign in the CampaignTabBar, styled after BoostListingButton.
 * Uses the shared Button component with outlineBrand variant and rect shape.
 */
export const BoostListingTabButton = ({
  campaign,
  isActive,
  zoneIcon: ZoneIcon,
  zoneTooltip,
  compact,
  onClick,
}: BoostListingTabButtonProps) => {
  const budget = campaign.activeFlight?.flightBudget ?? 0;
  const spend = campaign.activeFlight?.flightSpend ?? 0;
  const remaining = budget - spend;
  const remainingPct = budget > 0 ? (remaining / budget) * 100 : 0;
  const remainingText = formatCurrency(
    remaining,
    campaign.currencyCode ?? 'USD'
  );
  const budgetText = formatCurrency(budget, campaign.currencyCode ?? 'USD');
  const tooltipText = `${remainingText} of ${budgetText} remaining budget`;

  return (
    <Button
      variant="outlineBrand"
      shape="rect"
      className={compact ? undefined : styles.campaignTabButton}
      size={compact ? 'unset' : undefined}
      style={
        compact
          ? { borderRadius: vars.borderRadius.s, padding: vars.spacing.xs }
          : undefined
      }
      color={!isActive ? vars.color.textErrorV2 : vars.color.textBrandV2}
      onClick={onClick}
      title={tooltipText}
    >
      <Stack gap="s" alignItems="center">
        <ZoneIcon size={compact ? '16px' : '24px'} title={zoneTooltip} />
        {!compact && (
          <Stack gap="s" alignItems="center" title={tooltipText}>
            <PosAnimatingProgressBar
              style={{
                width: '60px',
                backgroundColor: vars.color.surfacePrimary,
              }}
              enableGlow
              isError={!isActive}
              value={remainingPct}
            />
            <span
              style={{
                color: !isActive
                  ? vars.color.textErrorV2
                  : vars.color.textBrandV2,
              }}
            >
              {remainingText}
            </span>
          </Stack>
        )}
      </Stack>
    </Button>
  );
};

export type StartCampaignTabButtonProps = {
  onClick: MouseEventHandler;
  hasCampaigns: boolean;
  compact?: boolean;
};

/**
 * A dashed-border tab button for starting a new campaign, styled after BoostListingButton.
 * Shows "Boost Event" when no campaigns exist, "Add Campaign" when campaigns are present.
 */
export const StartCampaignTabButton = ({
  onClick,
  hasCampaigns,
  compact,
}: StartCampaignTabButtonProps) => {
  return (
    <Button
      variant="outlineBrand"
      shape="rect"
      className={compact ? undefined : styles.campaignTabButton}
      size={compact ? 'unset' : undefined}
      style={
        compact
          ? { borderRadius: vars.borderRadius.s, padding: vars.spacing.xs }
          : undefined
      }
      color={vars.color.textBrandV2}
      onClick={onClick}
    >
      <Stack gap="s" alignItems="center">
        <BoostIconDisplay size={compact ? '16px' : vars.iconSize.s} inactive />
        {!compact &&
          (hasCampaigns ? (
            <Content id={ContentId.AddCampaign} />
          ) : (
            <Content id={ContentId.BoostEvent} />
          ))}
      </Stack>
    </Button>
  );
};
